import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { PlusIcon, TrashIcon, EyeIcon, DownloadIcon, CheckIcon, XIcon, CashIcon, CheckCircleIcon, ClockIcon, DocumentTextIcon } from "@heroicons/react/solid";
import { useAdminPettyCashDetail } from "../../hooks/petty_cash/useAdminPettyCashDetail";
import { Button, DateInput } from "../../components/ui";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import { formatDateWithHyphens } from "../../utility";
import DeleteConfirmationModal from "../../components/ui/DeleteConfirmationModal";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import Pagination from "../../components/pagination";
import InlineLoader from "../../components/loader/InlineLoader";
import PettyCashForm from "./PettyCashForm";
import RoundedContainer from "../../components/containers/RoundedContainer";
import PettyCashDetailModal from "./PettyCashDetailModal";
import UpdateInvoiceNoModal from "./UpdateInvoiceNoModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";

function AdminPettyCashDetail() {
  const { id: employeeId } = useParams();

  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [openFormModal, setOpenFormModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null, transactionId: null, reason: "" });
  const [invoiceModal, setInvoiceModal] = useState({ open: false, transaction: null });

  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({ ...prev, open: false }));
    setTimeout(() => {
      setConfirmModal({ open: false, type: null, transactionId: null, reason: "" });
    }, 300);
  };

  const {
    transactionListing,
    statisticsListing,
    employeeInfo,
    activeTxnQuery,
    filters,
    searchQuery,
    selectedTxnId,
    setSelectedTxnId,
    isProcessingApproval,
    isDeleting,
    isExporting,
    handleViewDetails,
    updateApprovalStatusHandler,
    deleteTransactionHandler,
    exportReportHandler,
    handlePageClick,
    handleFilterChange,
    handleSearchChange,
  } = useAdminPettyCashDetail({
    employeeId,
    onApprovalSuccess: () => {
      closeConfirmModal();
    },
    onDeleteSuccess: () => {
      setDeleteModal({ open: false, id: null });
    },
  });

  const stats = statisticsListing.data?.data?.statistics || statisticsListing.data?.data || {};
  const totalIncome = parseFloat(stats.total_income || 0);
  const totalExpenses = parseFloat(stats.total_approved_expenses || 0);
  const totalPending = parseFloat(stats.total_pending_expenses || 0);

  const handleApproveClick = (transactionId) => {
    setConfirmModal({ open: true, type: "APPROVE", transactionId, remarks: "" });
  };

  const handleConfirmModalClose = () => {
    setConfirmModal((prev) => ({ ...prev, open: false }));
    setTimeout(() => {
      setConfirmModal({ open: false, type: null, transactionId: null, reason: "" });
    }, 300);
  };

  const handleConfirmModalAction = () => {
    if (confirmModal.type === "APPROVE") {
      updateApprovalStatusHandler({ transactionId: confirmModal.transactionId, status: "APPROVED" });
    } else if (confirmModal.type === "REJECT") {
      updateApprovalStatusHandler({
        transactionId: confirmModal.transactionId,
        status: "REJECTED",
        remarks: confirmModal.reason,
      });
    }
  };

  const activeTransaction = activeTxnQuery.data?.data?.[0];
  const isLoadingDetail = activeTxnQuery.isLoading;
  const transactions = transactionListing.data?.data?.transaction_data || [];

  return (
    <div className="py-6 px-4 md:px-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <BreadCrumbs
          crumbs={[
            { label: "Dashboard", link: "/" },
            { label: "Petty Cash", link: "/" },
            { label: `${employeeInfo?.employee_name || "Employee"}'s Ledger`, link: "" },
          ]}
        />
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                {employeeInfo?.employee_name || "Employee"}'s Ledger
              </h1>
              <p className="text-gray-500 text-xs md:text-sm mt-0.5">
                {employeeInfo?.email}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={() => exportReportHandler()} disabled={isExporting} className="inline-flex items-center space-x-2">
              <DownloadIcon className="w-4 h-4" />
              <span>Export Report</span>
              {isExporting && <InlineLoader className="ml-2" />}
            </Button>
            <Button onClick={() => setOpenFormModal(true)} className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700">
              <PlusIcon className="w-5 h-5" />
              <span>Add Transaction</span>
            </Button>
          </div>
        </div>

        {/* Statistics KPI Cards */}
        {employeeId && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="bg-white border border-gray-150 rounded-2xl p-5 shadow-sm flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Total Income</span>
                {statisticsListing.isLoading ? (
                  <div className="h-8 w-24 bg-gray-200 animate-pulse rounded-lg" />
                ) : (
                  <div className="text-2xl font-bold text-gray-900">&#8377;{totalIncome}</div>
                )}
              </div>
              <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                <CashIcon className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white border border-gray-150 rounded-2xl p-5 shadow-sm flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Approved Expenses</span>
                {statisticsListing.isLoading ? (
                  <div className="h-8 w-24 bg-gray-200 animate-pulse rounded-lg" />
                ) : (
                  <div className="text-2xl font-bold text-green-600">&#8377;{totalExpenses}</div>
                )}
              </div>
              <div className="p-3 rounded-xl bg-green-50 text-green-600">
                <CheckCircleIcon className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white border border-gray-150 rounded-2xl p-5 shadow-sm flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Pending Approvals</span>
                {statisticsListing.isLoading ? (
                  <div className="h-8 w-24 bg-gray-200 animate-pulse rounded-lg" />
                ) : (
                  <div className="text-2xl font-bold text-amber-600">&#8377;{totalPending}</div>
                )}
              </div>
              <div className="p-3 rounded-xl bg-amber-50 text-amber-600">
                <ClockIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
        )}



        {/* Filter Bar */}
        <RoundedContainer className="p-5 shadow-sm border border-gray-150 mt-0 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search particulars..."
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Status</label>
              <select
                value={filters.approval_status}
                onChange={(e) => handleFilterChange("approval_status", e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">From Date</label>
              <DateInput
                label={null}
                value={filters.from_date}
                onChange={(date) => handleFilterChange("from_date", date ? formatDateWithHyphens(date) : "")}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                placeholderText="Select Date"
                isClearable
                selectsStart
                startDate={filters.from_date ? new Date(filters.from_date) : null}
                endDate={filters.to_date ? new Date(filters.to_date) : null}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">To Date</label>
              <DateInput
                label={null}
                value={filters.to_date}
                onChange={(date) => handleFilterChange("to_date", date ? formatDateWithHyphens(date) : "")}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                placeholderText="Select Date"
                isClearable
                selectsEnd
                startDate={filters.from_date ? new Date(filters.from_date) : null}
                endDate={filters.to_date ? new Date(filters.to_date) : null}
                minDate={filters.from_date ? new Date(filters.from_date) : null}
              />
            </div>
          </div>
        </RoundedContainer>

        {/* Transaction Table / Card List */}
        {/* Transaction Table / Card List */}
        {transactionListing.isLoading ? (
          <div className="flex items-center justify-center py-12 w-full bg-white rounded-2xl border border-gray-150 shadow-sm min-h-[200px]">
            <InlineLoader className="w-8 h-8 text-primary" />
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <RoundedContainer className="hidden md:block border border-gray-150 shadow-sm p-4">
              <div className="max-w-full overflow-x-auto">
                <Table className="table-auto w-full">
                  <TableHead>
                    <TableHeaderCell>Sr.no</TableHeaderCell>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell>Particulars</TableHeaderCell>
                    <TableHeaderCell>Category</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Amount</TableHeaderCell>
                    <TableHeaderCell className="text-center">Status</TableHeaderCell>
                    <TableHeaderCell className="text-center">Actions</TableHeaderCell>
                  </TableHead>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-gray-400">
                          No transactions found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((txn, idx) => (
                        <TableRow key={txn.transaction_id}>
                          <TableCell>
                            {filters.page_number * filters.limit + idx + 1}
                          </TableCell>
                          <TableCell className="text-gray-800 font-medium">{txn.expenses_date}</TableCell>
                          <TableCell className="text-gray-600">
                            <div className="flex items-center gap-2">
                              <span>{txn.particulars}</span>
                              {txn.is_invoice_no_present === false && (
                                <span
                                  title="Invoice missing"
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 text-xs font-bold flex-shrink-0"
                                >
                                  i
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600 font-medium">{txn.expenses_type_name || "-"}</TableCell>
                          <TableCell>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${txn.transaction_type === "CR" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                              }`}>
                              {txn.transaction_type === "CR" ? "CR" : "DR"}
                            </span>
                          </TableCell>
                          <TableCell className="font-semibold text-gray-800">&#8377;{txn.amount}</TableCell>
                          <TableCell className="text-center">
                            <span className={
                              txn.approval_status === "APPROVED" ? "status-approved" :
                                txn.approval_status === "PENDING" ? "status-pending" : "status-rejected"
                            }>
                              {txn.approval_status}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => handleViewDetails(txn.transaction_id)}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                title="View Details"
                              >
                                <EyeIcon className="w-5 h-5" />
                              </button>
                              {(txn.approval_status === "PENDING" || txn.approval_status === "REJECTED") && (
                                <button
                                  onClick={() => handleApproveClick(txn.transaction_id)}
                                  className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                                  title="Approve"
                                >
                                  <CheckIcon className="w-5 h-5" />
                                </button>
                              )}
                              {(txn.approval_status === "PENDING" || txn.approval_status === "APPROVED") && (
                                <button
                                  onClick={() => setConfirmModal({ open: true, type: "REJECT", transactionId: txn.transaction_id, reason: "" })}
                                  className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                  title="Reject"
                                >
                                  <XIcon className="w-5 h-5" />
                                </button>
                              )}
                              <button
                                onClick={() => setDeleteModal({ open: true, id: txn.transaction_id })}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => setInvoiceModal({ open: true, transaction: txn })}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                title={txn.invoice_no ? "Update Invoice No" : "Add Invoice No"}
                              >
                                <DocumentTextIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </RoundedContainer>

            {/* Mobile Card List View */}
            <div className="md:hidden space-y-4">
              {transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-400 bg-white rounded-2xl border border-gray-150">
                  No transactions found.
                </div>
              ) : (
                transactions.map((txn) => (
                  <div
                    key={txn.transaction_id}
                    className="bg-white rounded-2xl border border-gray-150 p-5 shadow-sm flex flex-col space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-wider ${txn.transaction_type === "CR" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          }`}>
                          {txn.transaction_type}
                        </span>
                        <span className="text-xs text-gray-400 font-semibold">{txn.expenses_date}</span>
                      </div>
                      <span className={
                        txn.approval_status === "APPROVED" ? "status-approved text-[10px] py-0.5 px-2" :
                          txn.approval_status === "PENDING" ? "status-pending text-[10px] py-0.5 px-2" :
                            "status-rejected text-[10px] py-0.5 px-2"
                      }>
                        {txn.approval_status}
                      </span>
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-800 font-bold block leading-tight">{txn.particulars}</span>
                        {txn.is_invoice_no_present === false && (
                          <span
                            title="Invoice missing"
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 text-xs font-bold flex-shrink-0"
                          >
                            i
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 font-semibold">{txn.expenses_type_name || "General"}</span>
                    </div>

                    {/* Actions for Pending or details */}
                    <div className="flex flex-col space-y-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-black text-gray-900">&#8377;{txn.amount}</span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewDetails(txn.transaction_id)}
                            className="p-2 rounded-xl text-gray-500 bg-gray-50 border border-gray-200 active:scale-95 transition-all"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setDeleteModal({ open: true, id: txn.transaction_id })}
                            className="p-2 rounded-xl text-red-500 bg-red-50 border border-red-100 active:scale-95 transition-all"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setInvoiceModal({ open: true, transaction: txn })}
                            className="p-2 rounded-xl text-amber-500 bg-amber-50 border border-amber-100 active:scale-95 transition-all"
                            title={txn.invoice_no ? "Update Invoice" : "Add Invoice"}
                          >
                            <DocumentTextIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {txn.approval_status === "PENDING" && (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => handleApproveClick(txn.transaction_id)}
                            className="py-2 text-xs font-semibold rounded-xl bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setConfirmModal({ open: true, type: "REJECT", transactionId: txn.transaction_id, reason: "" })}
                            className="py-2 text-xs font-semibold rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors shadow-sm"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      {txn.approval_status === "APPROVED" && (
                        <button
                          onClick={() => setConfirmModal({ open: true, type: "REJECT", transactionId: txn.transaction_id, reason: "" })}
                          className="w-full py-2 text-xs font-semibold rounded-xl bg-red-600 text-white hover:bg-red-750 transition-colors shadow-sm"
                        >
                          Reject Transaction
                        </button>
                      )}
                      {txn.approval_status === "REJECTED" && (
                        <button
                          onClick={() => handleApproveClick(txn.transaction_id)}
                          className="w-full py-2 text-xs font-semibold rounded-xl bg-green-600 text-white hover:bg-green-750 transition-colors shadow-sm"
                        >
                          Approve Transaction
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <RoundedContainer className="border border-gray-150 shadow-sm p-4">
              <Pagination
                currentPage={filters.page_number}
                lengthofItems={transactionListing.data?.total_count || 0}
                limit={filters.limit}
                onPageChange={handlePageClick}
              />
            </RoundedContainer>
          </>
        )}
      </div>

      {/* ----------------- DELETE CONFIRMATION MODAL ----------------- */}
      <DeleteConfirmationModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onDelete={() => deleteTransactionHandler(deleteModal.id)}
        isLoading={isDeleting}
        message="Are you sure you want to delete this petty cash transaction?"
      />

      {/* ----------------- CONFIRMATION MODAL (Approve/Reject) ----------------- */}
      <ConfirmationModal
        isOpen={confirmModal.open}
        onClose={handleConfirmModalClose}
        title={confirmModal.type === "APPROVE" ? "Confirm Approval" : "Confirm Rejection"}
        description={
          confirmModal.type === "APPROVE"
            ? "Are you sure you want to approve this transaction?"
            : "Are you sure you want to reject this transaction? Please provide a reason."
        }
        confirmButtonLabel={confirmModal.type === "APPROVE" ? "Approve" : "Reject"}
        onConfirm={handleConfirmModalAction}
        isLoading={isProcessingApproval}
        loadingMessage={confirmModal.type === "APPROVE" ? "Approving..." : "Rejecting..."}
        showReasonField={confirmModal.type === "REJECT"}
        reason={confirmModal.reason}
        onReasonChange={(value) => setConfirmModal((prev) => ({ ...prev, reason: value }))}
        reasonLabel="Reason for rejection"
        reasonPlaceholder="e.g. Missing valid receipt."
      />

      {/* ----------------- VIEW TRANSACTION DETAILS MODAL ----------------- */}
      <PettyCashDetailModal
        open={!!selectedTxnId}
        onClose={() => setSelectedTxnId(null)}
        transaction={activeTransaction}
        isLoading={isLoadingDetail}
      />

      {/* Add Transaction Modal */}
      {openFormModal && (
        <PettyCashForm
          open={openFormModal}
          onClose={() => setOpenFormModal(false)}
          companyEmployeeId={employeeId}
          onSuccess={() => { }}
        />
      )}

      {/* Update Invoice Number Modal */}
      <UpdateInvoiceNoModal
        open={invoiceModal.open}
        onClose={() => setInvoiceModal({ open: false, transaction: null })}
        transaction={invoiceModal.transaction}
        refreshUrl={["petty-cash/transaction_listing", "petty-cash/fetch_single_transaction"]}
      />
    </div>
  );
}

export default AdminPettyCashDetail;
